"use client";

import { useEffect, useState } from "react";
import { Button, Loader, Text, Title, Box as MantineBox } from "@mantine/core";

import Link from "@/components/Link";
import { loadCurrentProduct } from "@/lib/client/product";
import PageContent from "./pageContent";
import styles from "./styles.module.scss";

const CLIENT_PRODUCT_TIMEOUT_MS = 15_000;

const ClientProductFallback = ({ id }: { id: number }) => {
  const [item, setItem] = useState<SearchResultItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      controller.abort();
      setLoading(false);
    }, CLIENT_PRODUCT_TIMEOUT_MS);

    setLoading(true);
    loadCurrentProduct(id, controller.signal)
      .then((currentItem) => {
        if (!controller.signal.aborted && currentItem) {
          setItem(currentItem);
        }
      })
      .catch(() => {
        // The retry state below handles transient client-side failures.
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt, id]);

  if (item) {
    return <PageContent item={item} />;
  }

  return (
    <MantineBox className={styles.loadingContainer} role="status" aria-live="polite">
      {loading ? (
        <>
          <Loader aria-label="Loading product" />
          <Text>Loading product…</Text>
        </>
      ) : (
        <>
          <Title order={2}>This product is taking longer than expected</Title>
          <Text>Try loading it again, or continue browsing while the service recovers.</Text>
          <MantineBox className={styles.fallbackActions}>
            <Button onClick={() => setAttempt((current) => current + 1)}>Try again</Button>
            <Button component={Link} href="/search" variant="subtle">
              Browse KURB
            </Button>
          </MantineBox>
        </>
      )}
    </MantineBox>
  );
};

export default ClientProductFallback;
